import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/src/components/ui/button";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PostCountryValues, PostCountrySchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { Country } from "../model";
import { FormErrors } from "../components/FormErrors";

export function AddCountry() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onSubmit = async (data: PostCountryValues) => {
    try {
      const response = await fetch<{ data: { id: string } }>("/api/locations/countries", "POST", data);
      toast({ title: "Success", description: "Country added successfully" });
      navigate(`/locations/countries/${response.data.id}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to add country", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-2xl shadow-md bg-white p-4">
      <h2 className="text-lg font-semibold">Add Country</h2>
      <EditCountryForm onSubmit={onSubmit} />
    </div>
  );
}

export function EditCountryForm({ onSubmit }: { onSubmit: (data: PostCountryValues) => void }) {
  const { fetch } = useFetch();

  const loadTopLevelRegions = async (q: string) => {
    const data = await fetch<{ data: Country[] }>(
      `/api/locations/top-level-regions?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const methods = useForm<PostCountryValues>({
    resolver: zodResolver(PostCountrySchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
        <div>
          <Label>Names</Label>
          <Textarea {...register("names")} placeholder="Enter names, one per line" />
        </div>

        <div>
          <Label>Code</Label>
          <Textarea {...register("code")} placeholder="Country Code" />
        </div>

        <RelationSingleInput name="tlr" label="Top-Level Region" loadOptions={loadTopLevelRegions} />

        <FormErrors errors={errors} />

        <Button type="submit">Save</Button>
      </form>
    </FormProvider>
  );
}
